import React, { FC, useState } from 'react';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { Form } from 'informed';
import { Button } from '@unbooking/ui-modules';
import { IDropdownOption } from '@ui-modules/types';
import './styles.scss';

import { useRepository } from '@context';
import { useDrivers, useFacility } from '@common/hooks';

import {
  IVehicleDetails,
  IVehicleDetailsApi,
  VehicleOwner,
  VehicleProtection,
} from '@common/interfaces';
import { CheckMark, GPSConnect, VehiclePhohoPlaceholder } from '@assets/svg/icons';
import {
  CityField,
  CountryField,
  PreferredDriverField,
  VehicleAgencyField,
  VehicleChassisNumberField,
  VehicleGvlpNumberField,
  VehicleModelField,
  VehicleOwnerField,
  VehiclePaxField,
  VehiclePlateNumberField,
  VehicleProtectionField,
  VehicleRoofNumberField,
  VehicleSharedAgencyField,
} from '../fields';

export interface FormVehicleEdit {
  chassisNumber: string;
  gvlpNumber: string;
  maxCapacity: IDropdownOption;
  plateNumber: string;
  preferredDriver: IDropdownOption;
  protection: IDropdownOption;
  roofNumber: string;
  sharedResources: IDropdownOption[];
  vehicleOwner: IDropdownOption;
}

export interface IVehicleEditForm {
  readOnly?: boolean;
  vehicle: IVehicleDetails;
  onSubmit: ({ vehicleId, data }: { vehicleId: string; data: Partial<IVehicleDetailsApi> }) => void;
}

const VehicleEditForm: FC<IVehicleEditForm> = ({ readOnly = false, vehicle, onSubmit }) => {
  const {
    facility: { city, country, facilityId, sharedWithAgencies },
  } = useFacility();
  const { t } = useTranslation();

  const { mobilityRepository } = useRepository();
  const { driverOptions } = useDrivers();

  const {
    agency,
    chassisNumber,
    connectedToGps,
    gvlpNumber,
    label,
    maxCapacity,
    model,
    modelChoices,
    plateNumber,
    preferredDriver,
    protection,
    roofNumber,
    vehicleOwner,
    sharedResources,
  } = vehicle;

  const [gpsError, setGpsError] = useState<string | null>(null);
  const [isGPSConnected, setIsGPSConnected] = useState<boolean>(connectedToGps);

  const { mutate: onGPSConnection, isLoading } = useMutation<unknown, AxiosError>(
    'connect-gps',
    () => mobilityRepository.vehicleConnectGPS(facilityId, vehicle.id, connectedToGps),
    {
      onSuccess: () => {
        setIsGPSConnected(!isGPSConnected);
        setGpsError(null);
      },
      onError: (error) => {
        if (error) {
          setGpsError(t('common.errorMessageGps'));
        }
      },
    },
  );

  return (
    <Form
      className="form-vehicle-edit modal-light-form"
      onSubmit={(formState: Record<string, unknown>) => {
        const state = { ...(formState.values as FormVehicleEdit) };

        onSubmit({
          vehicleId: vehicle.id,
          data: {
            chassis_number: state.chassisNumber || '',
            gvlp_number: state.gvlpNumber,
            max_capacity: +state.maxCapacity.value,
            plate_number: state.plateNumber,
            preferred_driver: state.preferredDriver,
            protection: state.protection.value as VehicleProtection,
            roof_number: state.roofNumber,
            vehicle_owner: state.vehicleOwner.value as VehicleOwner,
            ...(state.sharedResources?.length
              ? {
                  shared_resources: state.sharedResources,
                }
              : {}),
          },
        });
      }}
    >
      <h2>{t('mobility.editVehicle')}</h2>
      <div className="row vehicle">
        <div className="vehicle-photo">
          {model?.image ? (
            <img src={model.image} alt={model.name} />
          ) : (
            <div className="vehicle-photo-placeholder">
              <VehiclePhohoPlaceholder />
            </div>
          )}
        </div>

        <div className="vehicle-info">
          <div className="name">{model ? model.name : label}</div>
          {!readOnly ? (
            <div className="gps-provider">
              {isGPSConnected ? (
                <>
                  <Button
                    className="btn-connected"
                    icon={<CheckMark />}
                    text={t('mobility.btnConnected')}
                    variant="submit"
                  />
                  <Button
                    className="btn-disconnect"
                    text={t('mobility.btnDisconnect')}
                    variant="link"
                    onClick={() => onGPSConnection?.()}
                  />
                </>
              ) : (
                <>
                  {isLoading ? (
                    <Button variant="secondary" text="Connecting…" />
                  ) : (
                    <Button
                      icon={<GPSConnect />}
                      text={t('mobility.btnConnectToGPS')}
                      variant="primary"
                      onClick={() => onGPSConnection?.()}
                    />
                  )}
                  <div className="gps-error">{gpsError}</div>
                </>
              )}
            </div>
          ) : null}
        </div>

        <VehicleSharedAgencyField
          defaultValue={sharedResources}
          disabled={readOnly}
          options={sharedWithAgencies}
        />
        <PreferredDriverField
          defaultValue={preferredDriver || undefined}
          disabled={readOnly}
          options={driverOptions}
        />
      </div>

      <h3>{t('mobility.carInfo')}</h3>
      <div className="row">
        <VehicleModelField defaultValue={model?.id} disabled options={modelChoices} />
        <VehicleChassisNumberField defaultValue={chassisNumber} disabled={readOnly} />
        <VehicleAgencyField defaultValue={agency?.shortName ?? ''} disabled />
        <VehiclePlateNumberField defaultValue={plateNumber} disabled={readOnly} />
      </div>

      <div className="row">
        <VehicleRoofNumberField defaultValue={roofNumber} disabled={readOnly} />
        <VehicleGvlpNumberField defaultValue={gvlpNumber} disabled={readOnly} />
        <VehiclePaxField defaultValue={maxCapacity} disabled={readOnly} />
        <VehicleOwnerField defaultValue={vehicleOwner} disabled={readOnly} />
        <VehicleProtectionField defaultValue={protection} disabled={readOnly} />
      </div>

      <h3>{t('mobiity.assignedTo')}</h3>
      <div className="row">
        <CountryField defaultValue={country} disabled />
        <CityField defaultValue={city} disabled />
      </div>

      <div className="footer">
        {!readOnly ? (
          <Button
            className="btn btn-submit"
            text={t('common.btnConfirmChanges')}
            type="submit"
            variant="submit"
          />
        ) : null}
      </div>
    </Form>
  );
};

export default VehicleEditForm;
